import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject, throwError, timer } from 'rxjs';
import { Component, inject, input, OnDestroy } from '@angular/core';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { DoodOrderModel } from '@store/order/order.model';
import { IWorldlinePaymentIntent } from '@core/models/payment.model';
import { PaymentMethodHandles } from '@config/payment-methods.config';

import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';

import { OrdersService } from '@core/services/orders/orders.service';
import { PaymentService } from '@core/services/payment/payment.service';

@Component({
  template: ``,
  selector: 'app-payment-panel-worldline',
  standalone: false,
})
export class PaymentPanelWorldlineComponent implements OnDestroy {
  readonly type = input.required<string>();
  readonly isUnique = input<boolean>(false);

  protected readonly unsubscribe$ = new Subject<boolean>();
  order: DoodOrderModel | null = null;

  private readonly orderDispatcher = inject(OrderStoreDispatcher);
  private readonly ordersService = inject(OrdersService);
  private readonly paymentService = inject(PaymentService);
  private readonly paymentDispatcher = inject(PaymentStoreDispatcher);
  private readonly translateService = inject(TranslateService);

  constructor() {
    timer(1).subscribe(() => {
      this.paymentDispatcher.updateUI({
        button: {
          text: 'worldline.pay-button',
          isEnabled: true,
        },
        status: {
          isLoading: false,
        },
      });
    });

    this.paymentService.pay$
      .pipe(takeUntil(this.unsubscribe$), take(1))
      .subscribe(() => this.handlePayment());

    if (this.isUnique()) {
      timer(1).subscribe(() => {
        this.paymentDispatcher.updateButton({
          text: 'Redirection vers Worldline...',
        });
      });
      this.handlePayment();
    }
  }

  ngOnDestroy(): void {
    this.paymentDispatcher.resetUI();
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  private handlePayment(): void {
    this.paymentDispatcher.updateUI({
      button: {
        isEnabled: false,
      },
      status: {
        isInProgress: true,
      },
    });

    this.ordersService
      .createOrder$(PaymentMethodHandles.Worldline)
      .pipe(
        tap(order => (this.order = order)),
        switchMap(order => (order ? this.getPaymentPageUrl(order) : of(null))),
        takeUntil(this.unsubscribe$),
        take(1),
        tap(() => this.orderDispatcher.resetErrors()),
        catchError(error => {
          this.orderDispatcher.resetValidation();
          this.orderDispatcher.updateErrors({
            error,
          });
          return throwError(() => error);
        }),
      )
      .subscribe(
        paymentPageUrl => {
          this.paymentDispatcher.updateUI({
            button: {
              isEnabled: true,
            },
            status: {
              isLoading: false,
            },
          });

          if (paymentPageUrl) {
            this.redirectToWorldline(paymentPageUrl);
          }
        },
        () => {
          this.paymentDispatcher.updateUI({
            error: this.translateService.instant('payment.payment-error'),
            button: {
              isEnabled: true,
            },
            status: {
              isLoading: false,
              isInProgress: false,
            },
          });
        },
      );
  }

  private redirectToWorldline(paymentPageUrl: string): void {
    window.location.href = paymentPageUrl;
  }

  private getPaymentPageUrl(order: DoodOrderModel): Observable<string> {
    return this.paymentService
      .createOrderPaymentIntent$<IWorldlinePaymentIntent>(order.id)
      .pipe(map(result => result.payment_page_url));
  }
}
